import { makeObservable, observable, flow, action, IObservableArray, computed } from "mobx";
import { ClientService, ReservationService, VisitService } from '@common/services';
import { AppointmentManagementGroupingModel, AppointmentManagementItem, AppointmentManagementListModel, IReservationCancelRequest, CommunicationPreferenceModel } from "@common/models";
import { ListUtility } from "@common/utils";
import { NotificationStoreInstance } from "@common/stores";

export class AppointmentsStore {
    jackpotReservationBookingUrl: string;
    /**Public Reservation Id */
    publicId: string = '';
    isReschedule: boolean = false;
    isRebook: boolean = false;

    @observable cancellationVisible: boolean = false;
    @observable confirmationVisible: boolean = false;
    @observable apptList: IObservableArray<AppointmentManagementListModel> = observable([]);
    @observable selectedItems: IObservableArray<AppointmentManagementItem> = observable([]);
    @observable clinicDate: Date | undefined;
    @observable sendToSalk: boolean = false;

    constructor(jackpotReservationBookingUrl: string | undefined) {

        this.jackpotReservationBookingUrl = jackpotReservationBookingUrl + '?source=customerapp';

        makeObservable<AppointmentsStore>(this, undefined, {
            autoBind: true
        });
    }

    @computed get petNames() {
        const lstPets = _.map(this.selectedItems, 'petName');
        const petNames = ListUtility.toOxford(lstPets, 'and', '');
        return petNames;
    }

    // #region Cancellation
    @action.bound showCancellation(items: AppointmentManagementItem[], isReschedule: boolean, isRebook: boolean): void {
        this.selectedItems.push(...items);
        this.isReschedule = isReschedule;
        this.isRebook = isRebook;
        this.cancellationVisible = true;
    }

    @action.bound closeCancellation(): void {
        this.selectedItems.clear();
        this.isReschedule = false;
        this.isRebook = false;
        this.cancellationVisible = false;
    }

    @action.bound cancelReservationConfirm() {
        // Map returns Array<string | undefined>
        // Compact strips out all falsey values including undefined and ""
        const ids = _.compact(_.map(this.selectedItems, 'reservationPublicId'));

        this.cancelAppointmentGroup(ids, this.isReschedule, this.isRebook).then(() => {
            this.getAppointmentAndVisitData();

            this.selectedItems.clear();
            if (this.isReschedule) {
                this.sendToSalk = true;
            }
            this.cancellationVisible = false;
        });
    }

    /**
     * Handles bulk cancellation on appointment group
     */
    cancelAppointmentGroup = flow(function* (this: AppointmentsStore, ids: string[], isReschedule: boolean, isRebook: boolean) {
        let request: IReservationCancelRequest = {
            publicReservationIds: ids,
            isReschedule: isReschedule,
            isRebook: isRebook
        };

        // cancel reservations
        yield ReservationService.cancelBulk(request);
    });
    // #endregion

    // #region Confirmation
    @action.bound showConfirmation(): void {
        this.confirmationVisible = true;
    }

    @action.bound closeConfirmation(): void {
        this.selectedItems.clear();
        this.sendToSalk = false;
        this.confirmationVisible = false;
    }

    @action.bound confirm(): void {
        this.getAppointmentAndVisitData();
        this.closeConfirmation();
    }

    /**
     * Handles Confirmation of appointment groups
     */
    @action.bound confirmAppointmentGroup = flow(function* (this: AppointmentsStore, group: AppointmentManagementGroupingModel) {
        // Map returns Array<string | undefined>
        // Compact strips out all falsey values including undefined and ""
        const ids = _.compact(_.map(group.items, 'reservationPublicId'));

        yield ReservationService.confirmBulk(ids, { showSuccess: true, showError: true }).then(() => {
            this.showConfirmation();
        });
    });
    // #endregion

    /**
     * Gets Appointment and Visit Data for a public customer
    */
    getAppointmentAndVisitData = flow(function* (this: AppointmentsStore) {
        this.apptList = yield ClientService.getAppointmentAndVisitData(this.publicId, undefined);
    });

    resendVisitCompletionEmail = flow(function* (this: AppointmentsStore, visitIds: string[]) {
        yield VisitService.resendVisitCompletionEmail(visitIds).then(() => {
            NotificationStoreInstance.success('We\'ve queued your Post Visit Summary email(s).');
        }).catch(() => {
            NotificationStoreInstance.error('An unknown error has occurred.');
        });
    });

    saveCommunicationPreference = flow(function* (this: AppointmentsStore, data: CommunicationPreferenceModel) {
        yield ClientService.saveCommunicationPreference(this.publicId, data)
            .then(() => {
                this.getAppointmentAndVisitData();
                NotificationStoreInstance.success('Communication preference saved.');
            }).catch(() => {
                NotificationStoreInstance.error('Failed to save communication preference.');
            });
    });

    @computed get latestCommunicationPreference(): CommunicationPreferenceModel {
        return this.apptList?.length > 0 ? this.apptList[0].communicationPreference : new CommunicationPreferenceModel();
    }

}
